import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BarChart3, Check, Download, Eye, Inbox, Loader2, LogOut, Lock, Mail, Phone, RotateCcw, Search, StickyNote, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API });
http.interceptors.request.use((config) => {
  const t = localStorage.getItem("adw-token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const inputCls =
  "w-full rounded-xl bg-white/[0.035] border border-white/[0.09] px-4 py-3.5 text-sm text-white placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/60 focus:ring-2 focus:ring-violet-500/20 transition-all";

const LoginCard = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await http.post("/auth/login", { email, password });
      if (r.data?.token) localStorage.setItem("adw-token", r.data.token);
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="admin-login">
      <div className="glass rounded-3xl p-8 md:p-10">
        <span className="inline-flex w-12 h-12 rounded-2xl btn-gradient items-center justify-center mb-6">
          <Lock size={20} className="text-white" />
        </span>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">
          Espace <span className="text-gradient">privé.</span>
        </h1>
        <p className="text-[var(--text-secondary)] text-sm mt-3 leading-relaxed">
          Réservé à l'équipe Les Amis du Web. Connectez-vous pour consulter les demandes et les statistiques.
        </p>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
          <input type="email" className={inputCls} placeholder="Adresse email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
          <input type="password" className={inputCls} placeholder="Mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
          <button type="submit" disabled={loading} className="btn-gradient rounded-full px-6 py-3.5 font-semibold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading && <Loader2 size={16} className="animate-spin" />}
            Se connecter
          </button>
        </form>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="card-surface rounded-2xl p-6" data-testid={testid}>
    <Icon size={18} className="text-[var(--accent-cyan)] mb-4" />
    <p className="font-display font-extrabold text-3xl md:text-4xl text-gradient">{value}</p>
    <p className="text-xs text-[var(--text-muted)] mt-2 font-mono-label tracking-[0.15em] uppercase">{label}</p>
  </div>
);

const WeekChart = ({ data }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="card-surface rounded-2xl p-6" data-testid="admin-week-chart">
      <div className="flex items-center gap-2 mb-5">
        <BarChart3 size={16} className="text-[var(--accent-violet)]" />
        <p className="text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)]">Visites — 7 derniers jours</p>
      </div>
      <div className="flex items-end gap-2 h-32">
        {data.map((d) => (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
            <span className="text-[10px] text-[var(--text-secondary)]">{d.count}</span>
            <div
              className="w-full rounded-t-md bg-gradient-to-t from-blue-600/60 to-cyan-400/70 transition-all"
              style={{ height: `${Math.max(4, (d.count / max) * 100)}%` }}
            />
            <span className="text-[9px] text-[var(--text-muted)] font-mono-label">{d.day.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LeadNotes = ({ lead }) => {
  const [notes, setNotes] = useState(lead.notes || "");
  const [savedNotes, setSavedNotes] = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = notes !== savedNotes;

  const save = async () => {
    setSaving(true);
    try {
      await http.patch(`/leads/${lead.id}/notes`, { notes });
      setSavedNotes(notes);
      toast.success("Notes enregistrées.");
    } catch {
      toast.error("Impossible d'enregistrer les notes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 border-t border-white/[0.06] pt-4">
      <p className="flex items-center gap-1.5 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[var(--text-muted)] mb-2">
        <StickyNote size={11} className="text-[var(--accent-violet)]" /> Notes internes — visibles uniquement par vous
      </p>
      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Ex : rappelé le 12/06, devis envoyé, attend ses photos…"
        className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] px-3.5 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/50 transition-colors resize-y"
        data-testid={`lead-notes-${lead.id}`}
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="btn-gradient mt-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
          data-testid={`lead-notes-save-${lead.id}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Enregistrer les notes
        </button>
      )}
    </div>
  );
};

const LeadCard = ({ lead, onToggle }) => {
  const done = lead.status === "traitee";
  return (
    <div className={`card-surface rounded-2xl p-6 ${done ? "opacity-70" : ""}`} data-testid={`admin-lead-${lead.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-display font-bold text-lg">
              {lead.first_name} {lead.last_name}
              {lead.company ? <span className="text-[var(--text-muted)] font-normal text-sm"> — {lead.company}</span> : null}
            </p>
            <span
              className={`rounded-full px-3 py-1 font-mono-label text-[9px] tracking-[0.15em] uppercase border ${
                done ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-300" : "bg-amber-500/10 border-amber-400/30 text-amber-300"
              }`}
              data-testid={`lead-status-${lead.id}`}
            >
              {done ? "Traitée" : "À traiter"}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-[var(--text-secondary)]">
            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 hover:text-[var(--accent-cyan)] transition-colors">
              <Mail size={13} /> {lead.email}
            </a>
            <span className="inline-flex items-center gap-1.5"><Phone size={13} /> {lead.phone}</span>
          </div>
        </div>
        <div className="text-right">
          {lead.project_type && (
            <span className="inline-block rounded-full bg-violet-500/10 border border-violet-500/30 px-3 py-1 font-mono-label text-[9px] tracking-[0.15em] uppercase text-[var(--accent-violet)]">
              {lead.project_type}
            </span>
          )}
          <p className="text-[10px] text-[var(--text-muted)] mt-2 font-mono-label">
            {new Date(lead.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
      </div>
      {lead.activity && <p className="text-xs text-[var(--text-muted)] mt-3">Activité : {lead.activity}</p>}
      <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed whitespace-pre-line border-t border-white/[0.06] pt-3">{lead.message}</p>
      <LeadNotes lead={lead} />
      <button
        onClick={() => onToggle(lead)}
        className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
          done ? "glass text-[var(--text-secondary)] hover:text-white" : "btn-gradient text-white"
        }`}
        data-testid={`lead-toggle-${lead.id}`}
      >
        {done ? <RotateCcw size={13} /> : <Check size={13} />}
        {done ? "Remettre à traiter" : "Marquer comme traitée"}
      </button>
    </div>
  );
};

export default function Admin() {
  usePageMeta({ title: "Espace privé — Les Amis du Web" });
  const [status, setStatus] = useState("checking");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");

  const loadData = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([http.get("/admin/stats"), http.get("/leads")]);
      setStats(s.data);
      setLeads(l.data);
      setStatus("authed");
    } catch {
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    localStorage.removeItem("adw-token");
    setStatus("anon");
    setStats(null);
    setLeads([]);
  };

  const toggleStatus = async (lead) => {
    const next = lead.status === "traitee" ? "a_traiter" : "traitee";
    try {
      await http.patch(`/leads/${lead.id}/status`, { status: next });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status: next } : l)));
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const pending = leads.filter((l) => l.status !== "traitee").length;

  const exportCsv = () => {
    const headers = ["Date", "Statut", "Prénom", "Nom", "Entreprise", "Activité", "Email", "Téléphone", "Type de projet", "Site actuel", "Message", "Notes"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = leads.map((l) =>
      [
        new Date(l.created_at).toLocaleString("fr-FR"),
        l.status === "traitee" ? "Traitée" : "À traiter",
        l.first_name, l.last_name, l.company, l.activity, l.email, l.phone, l.project_type, l.current_website, l.message, l.notes,
      ].map(esc).join(";")
    );
    const csv = "\uFEFF" + [headers.map(esc).join(";"), ...rows].join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `demandes-les-amis-du-web-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${leads.length} demande(s) exportée(s) en CSV.`);
  };

  const visibleLeads = leads.filter((l) => {
    if (statusFilter === "a_traiter" && l.status === "traitee") return false;
    if (statusFilter === "traitee" && l.status !== "traitee") return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [l.first_name, l.last_name, l.company, l.activity, l.email, l.phone, l.project_type, l.message, l.notes]
      .filter(Boolean)
      .some((v) => v.toLowerCase().includes(q));
  });

  return (
    <div className="relative min-h-screen bg-[var(--bg-primary)]" data-testid="admin-page">
      <Header />
      <main className="relative pt-36 pb-28">
        <div className="glow-orb w-[460px] h-[460px] bg-blue-600/12 -top-24 -left-32" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative">
          {status === "checking" && (
            <div className="flex justify-center py-24">
              <Loader2 size={28} className="animate-spin text-[var(--accent-cyan)]" />
            </div>
          )}

          {status === "anon" && <LoginCard onSuccess={loadData} />}

          {status === "authed" && stats && (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="admin-dashboard">
              <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                <div>
                  <p className="section-label mb-4">— Espace privé</p>
                  <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">
                    Tableau de <span className="text-gradient">bord.</span>
                  </h1>
                </div>
                <button onClick={logout} className="glass inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-[var(--text-secondary)] hover:text-white transition-colors" data-testid="admin-logout-btn">
                  <LogOut size={14} /> Déconnexion
                </button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard icon={Eye} label="Visites totales" value={stats.visits_total} testid="stat-visits-total" />
                <StatCard icon={TrendingUp} label="Visites aujourd'hui" value={stats.visits_today} testid="stat-visits-today" />
                <StatCard icon={BarChart3} label="Visites sur 7 jours" value={stats.visits_last7.reduce((a, d) => a + d.count, 0)} testid="stat-visits-week" />
                <StatCard icon={Inbox} label="Demandes reçues" value={stats.leads_total} testid="stat-leads-total" />
              </div>

              <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5 mb-14">
                <WeekChart data={stats.visits_last7} />
                <div className="card-surface rounded-2xl p-6" data-testid="admin-top-pages">
                  <p className="text-xs font-mono-label tracking-[0.15em] uppercase text-[var(--text-muted)] mb-5">Pages les plus visitées</p>
                  <div className="flex flex-col gap-3">
                    {stats.top_pages.length === 0 && <p className="text-sm text-[var(--text-muted)]">Aucune visite enregistrée pour l'instant.</p>}
                    {stats.top_pages.map((p) => (
                      <div key={p.page} className="flex items-center justify-between text-sm">
                        <span className="text-[var(--text-secondary)] font-mono-label text-xs">{p.page}</span>
                        <span className="text-white font-semibold">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <h2 className="font-display font-bold text-2xl tracking-tight">
                  Demandes reçues <span className="text-[var(--text-muted)] text-lg">({leads.length})</span>
                  {pending > 0 && (
                    <span className="ml-3 rounded-full bg-amber-500/10 border border-amber-400/30 px-3 py-1 font-mono-label text-[10px] tracking-[0.15em] uppercase text-amber-300 align-middle" data-testid="pending-count">
                      {pending} à traiter
                    </span>
                  )}
                </h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1 max-w-md">
                  <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un nom, une activité, un email…"
                    className="w-full rounded-full bg-white/[0.035] border border-white/[0.09] pl-11 pr-4 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] outline-none focus:border-violet-500/50 transition-colors"
                    data-testid="leads-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  {[
                    { value: "tous", label: "Toutes" },
                    { value: "a_traiter", label: "À traiter" },
                    { value: "traitee", label: "Traitées" },
                  ].map((f) => (
                    <button
                      key={f.value}
                      onClick={() => setStatusFilter(f.value)}
                      className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                        statusFilter === f.value ? "btn-gradient text-white" : "glass text-[var(--text-secondary)] hover:text-white"
                      }`}
                      data-testid={`leads-filter-${f.value}`}
                    >
                      {f.label}
                    </button>
                  ))}
                  <button
                    onClick={exportCsv}
                    disabled={leads.length === 0}
                    className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-[var(--accent-cyan)] hover:bg-white/[0.07] transition-colors disabled:opacity-50"
                    data-testid="leads-export-csv"
                  >
                    <Download size={13} /> Exporter en CSV
                  </button>
                </div>
              </div>
              <div className="flex flex-col gap-4" data-testid="admin-leads-list">
                {visibleLeads.length === 0 && (
                  <p className="text-[var(--text-muted)]" data-testid="leads-empty">
                    {leads.length === 0 ? "Aucune demande pour le moment." : "Aucune demande ne correspond à votre recherche."}
                  </p>
                )}
                {visibleLeads.map((lead) => (
                  <LeadCard key={lead.id} lead={lead} onToggle={toggleStatus} />
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
